import { useState } from "react";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";
import { ActionModal } from "../WorklistActions";
import { UnserviceableModal } from "../UnserviceableModal";

/**
 * Shared "run the action picked in a line's Select action… dropdown" logic —
 * used by JobDetailModal AND by the inline drill-down tables in
 * My Worklist / Active Lab so a technician can action a specific equipment
 * line WITHOUT opening the full job. Mirrors JobDetailModal.runLineAction.
 */
// My Worklist no longer offers "Start Work" as its own menu entry — these
// are the actions that record real bench/parts work, so if a line hasn't
// been started yet, auto-start it first (non-fatal) rather than let it
// skip straight from Received/Assigned to a bench outcome in the history.
const AUTO_START_ACTIONS = new Set(["repaired", "adjusted", "calibrated", "parts_required"]);

export function useLineActionRunner(reload) {
  const [action, setAction] = useState(null); // {key, item}
  const [unserviceableId, setUnserviceableId] = useState(null);
  const [unserviceableDesc, setUnserviceableDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const runLineAction = async (key, item) => {
    if (key === "start_work") {
      setBusy(true);
      try {
        await api.post(`/job-items/${item.id}/start`);
        toast.success("Work started — status set to In Progress");
        reload();
      } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
      return;
    }
    if (key === "unserviceable") {
      setUnserviceableId(item.id);
      setUnserviceableDesc(item.description || "");
      return;
    }
    if (key === "back_to_progress" || key === "send_to_admin") {
      setBusy(true);
      try {
        const newStatus = key === "send_to_admin" ? "ready_for_admin" : "in_progress";
        await api.patch(`/job-items/${item.id}/status`, { status: newStatus });
        toast.success(key === "send_to_admin"
          ? "Sent to Admin — removed from the technician queue"
          : "Sent back to In Progress for rework");
        reload();
      } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
      return;
    }
    if (AUTO_START_ACTIONS.has(key) && ["received", "assigned"].includes(item.status)) {
      try { await api.post(`/job-items/${item.id}/start`); } catch (e) { /* non-fatal — proceed regardless */ }
    }
    // calibrated / adjusted / repaired / parts_required / add_note → ActionModal
    setAction({ key, item });
  };

  const modals = (
    <>
      {action && (
        <ActionModal action={action.key} item={action.item}
          onClose={() => setAction(null)}
          onDone={() => reload()} />
      )}
      {unserviceableId && (
        <UnserviceableModal itemId={unserviceableId}
          itemDescription={unserviceableDesc}
          onClose={() => setUnserviceableId(null)}
          onDone={() => { setUnserviceableId(null); reload(); }} />
      )}
    </>
  );

  return { runLineAction, busy, modals };
}
